import * as React from "react";
import { useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Text } from "@fluentui/react-components";
import { Box, Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useFilterStyles } from "../styles/filterStyles";
import SpreetailHeader from "../layout/SpreetailHeader";
import BrandFilter from "../filters/controls/BrandFilter";
import SkusFilter from "../filters/controls/SkusFilter";
import KeywordFilter from "../filters/controls/KeywordFilter";
import { PRICE_CLUSTER_MODES } from "../../../shared/constants";

interface MarketWatchFilterViewProps {
  brand: string[];
  setBrand: (value: string[]) => void;
  asin: string[];
  setAsin: (value: string[]) => void;
  keyword: string[];
  setKeyword: (value: string[]) => void;
  brandOptions: string[];
  asinOptions: string[];
  keywordOptions: string[];
  brandLoading: boolean;
  asinLoading: boolean;
  keywordLoading: boolean;
  loading: boolean;
  error: string | null;
  onApplyFilter: () => void;
  onClearFilters: () => void;
  onBrandOpen: () => void;
  onAsinOpen: () => void;
  onKeywordOpen: () => void;
  onBrandClose?: () => void;
  onAsinClose?: () => void;
  onKeywordClose?: () => void;
  keywordDisabled?: boolean;
  excludedBrands: string[];
  onExcludedChange: (brands: string[]) => void;
  isExcludeSameBrand: boolean;
  onExcludeSameBrandChange: (value: boolean) => void;
  isExcludeSpreetailAsin: boolean;
  onExcludeSpreetailAsinChange: (value: boolean) => void;
  allBrands: string[];
  hasSelectedAsins: boolean;
  priceClusterMode: "global" | "dynamic";
  onPriceClusterModeChange: (mode: "global" | "dynamic") => void;
  priceVariancePct: number;
  varianceGrowthFactor: number | null;
  onPriceVariancePctChange: (value: number) => void;
  onVarianceGrowthFactorChange: (value: number | null) => void;
  clusterSettingsLoading: boolean;
  onNavigateToSettings?: () => void;
  isEnabledSeasonality: boolean;
  onEnabledSeasonalityChange: (value: boolean) => void;
}

const MarketWatchFilterView: React.FC<MarketWatchFilterViewProps> = ({
  brand,
  setBrand,
  asin,
  setAsin,
  keyword,
  setKeyword,
  brandOptions,
  asinOptions,
  keywordOptions,
  brandLoading,
  asinLoading,
  keywordLoading,
  loading,
  error,
  onApplyFilter,
  onClearFilters,
  onBrandOpen,
  onAsinOpen,
  onKeywordOpen,
  onBrandClose,
  onAsinClose,
  onKeywordClose,
  keywordDisabled,
  excludedBrands,
  onExcludedChange,
  isExcludeSameBrand,
  onExcludeSameBrandChange,
  isExcludeSpreetailAsin,
  onExcludeSpreetailAsinChange,
  allBrands,
  hasSelectedAsins,
  priceClusterMode,
  onPriceClusterModeChange,
  priceVariancePct,
  varianceGrowthFactor,
  onPriceVariancePctChange,
  onVarianceGrowthFactorChange,
  clusterSettingsLoading,
  onNavigateToSettings,
  isEnabledSeasonality,
  onEnabledSeasonalityChange,
}) => {
  const filterStyles = useFilterStyles();
  const history = useHistory();
  const errorRef = useRef<HTMLDivElement>(null);
  const [editingVariance, setEditingVariance] = React.useState<string>(String(priceVariancePct));
  const [editingGrowth, setEditingGrowth] = React.useState<string>(
    varianceGrowthFactor != null ? String(varianceGrowthFactor) : ""
  );
  const [varianceError, setVarianceError] = React.useState<string | null>(null);
  const [growthError, setGrowthError] = React.useState<string | null>(null);

  // Sync local edit state when props change (e.g., config fetch completes, sheet restore)
  React.useEffect(() => {
    setEditingVariance(String(priceVariancePct));
  }, [priceVariancePct]);

  React.useEffect(() => {
    setEditingGrowth(varianceGrowthFactor != null ? String(varianceGrowthFactor) : "");
  }, [varianceGrowthFactor]);

  const handleVarianceChange = (raw: string) => {
    setEditingVariance(raw);
    const num = parseFloat(raw);
    if (raw === "" || isNaN(num)) {
      setVarianceError("Required (5–30)");
      return;
    }
    if (num < 5 || num > 30) {
      setVarianceError("Must be 5–30");
      return;
    }
    setVarianceError(null);
    onPriceVariancePctChange(num);
  };

  const handleGrowthChange = (raw: string) => {
    setEditingGrowth(raw);
    if (raw === "") {
      setGrowthError("Required (1.0–50.0)");
      return;
    }
    const num = parseFloat(raw);
    if (isNaN(num)) {
      setGrowthError("Must be a number");
      return;
    }
    if (num < 1.0 || num > 50.0) {
      setGrowthError("Must be 1.0–50.0");
      return;
    }
    setGrowthError(null);
    onVarianceGrowthFactorChange(num);
  };

  const hasClusterErrors = priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC && (!!varianceError || !!growthError);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  return (
    <div
      className={filterStyles.filterContainer}
      style={{
        height: "100vh",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fixed Header */}
      <SpreetailHeader />

      {/* Scrollable Content (Title + Filters) */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "0 16px",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
        className="hidden-scrollbar"
      >
        <style>
          {`
            .hidden-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        {/* Title Section - Now Scrollable */}
        <div className={filterStyles.filterHeader}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Text size={500} weight="semibold" style={{ color: "#0F172A" }}>
              Market Watch
            </Text>
            <Tooltip title="Configure">
              <IconButton
                size="small"
                onClick={() => onNavigateToSettings ? onNavigateToSettings() : history.push("/configuration-settings")}
                aria-label="Configure feasibility weights"
                sx={{
                  color: "#9CA3AF",
                  marginTop: "2px",
                  "&:hover": { color: "#068E75", backgroundColor: "#F0FDF4" },
                }}
              >
                <SettingsIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
          <Text size={200} style={{ color: "#667085", opacity: 0.9, fontSize: "12px" }}>
            Apply filters to execute the market watch analysis
          </Text>
        </div>

        {error && (
          <div ref={errorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
            <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <p className="text-[13px] text-red-800 m-0 leading-snug">{error}</p>
          </div>
        )}

        {/* Brand Filter */}
        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
          <BrandFilter
            value={brand}
            onChange={setBrand}
            options={brandOptions}
            isLoading={brandLoading}
            onActivate={onBrandOpen}
            onDeactivate={onBrandClose}
            exclusionMode={true}
            excludedBrands={excludedBrands}
            onExcludedChange={onExcludedChange}
            allBrands={allBrands}
          />
        </Box>

        {/* ASIN/SKU Filter */}
        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
          <SkusFilter
            value={asin}
            onChange={setAsin}
            options={asinOptions}
            isLoading={asinLoading}
            onActivate={onAsinOpen}
            onDeactivate={onAsinClose}
          />
        </Box>

        {/* Keywords Filter */}
        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
          <KeywordFilter
            value={keyword}
            onChange={setKeyword}
            options={keywordOptions}
            isLoading={keywordLoading}
            onActivate={onKeywordOpen}
            onDeactivate={onKeywordClose}
            disabled={keywordDisabled}
          />
        </Box>

        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
            <label
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Price Cluster
            </label>
            <div style={{ display: "flex", width: "100%" }}>
              <button
                type="button"
                onClick={() => onPriceClusterModeChange(PRICE_CLUSTER_MODES.GLOBAL)}
                style={{
                  flex: 1,
                  padding: "7px 12px",
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  border: "1.5px solid #068E75",
                  borderRight: "none",
                  borderRadius: "8px 0 0 8px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor:
                    priceClusterMode === PRICE_CLUSTER_MODES.GLOBAL ? "#068E75" : "rgba(255, 255, 255, 0.6)",
                  color: priceClusterMode === PRICE_CLUSTER_MODES.GLOBAL ? "#FFFFFF" : "#068E75",
                }}
              >
                Automatic
              </button>
              <button
                type="button"
                onClick={() => onPriceClusterModeChange(PRICE_CLUSTER_MODES.DYNAMIC)}
                style={{
                  flex: 1,
                  padding: "7px 12px",
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  border: "1.5px solid #068E75",
                  borderRadius: "0 8px 8px 0",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor:
                    priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC ? "#068E75" : "rgba(255, 255, 255, 0.6)",
                  color: priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC ? "#FFFFFF" : "#068E75",
                }}
              >
                Manual
              </button>
            </div>
            {priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px",
                  backgroundColor: "rgba(6, 142, 117, 0.04)",
                  borderRadius: "8px",
                  border: "1px solid rgba(6, 142, 117, 0.15)",
                }}
              >
                {clusterSettingsLoading ? (
                  <p style={{ fontSize: "12px", color: "#9CA3AF", margin: 0 }}>
                    Loading cluster defaults…
                  </p>
                ) : (
                  <>
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#374151",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Variance %
                      </label>
                      <input
                        type="number"
                        value={editingVariance}
                        onChange={(e) => handleVarianceChange(e.target.value)}
                        min={5}
                        max={30}
                        step={1}
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          fontSize: "13px",
                          fontFamily: "inherit",
                          border: varianceError ? "1.5px solid #EF4444" : "1.5px solid #D1D5DB",
                          borderRadius: "6px",
                          outline: "none",
                          boxSizing: "border-box" as const,
                          backgroundColor: "#FFFFFF",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) => {
                          if (!varianceError) e.currentTarget.style.borderColor = "#068E75";
                        }}
                        onBlur={(e) => {
                          if (!varianceError) e.currentTarget.style.borderColor = "#D1D5DB";
                        }}
                      />
                      {varianceError && (
                        <p style={{ fontSize: "11px", color: "#EF4444", margin: "4px 0 0 0" }}>
                          {varianceError}
                        </p>
                      )}
                      <p style={{ fontSize: "11px", color: "#9CA3AF", margin: "4px 0 0 0" }}>
                        Band width around target price (5–30%)
                      </p>
                    </div>
                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#374151",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Growth Factor
                      </label>
                      <input
                        type="number"
                        value={editingGrowth}
                        onChange={(e) => handleGrowthChange(e.target.value)}
                        min={1.0}
                        max={50.0}
                        step={0.1}
                        placeholder="Enter Growth Factor"
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          fontSize: "13px",
                          fontFamily: "inherit",
                          border: growthError ? "1.5px solid #EF4444" : "1.5px solid #D1D5DB",
                          borderRadius: "6px",
                          outline: "none",
                          boxSizing: "border-box" as const,
                          backgroundColor: "#FFFFFF",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) => {
                          if (!growthError) e.currentTarget.style.borderColor = "#068E75";
                        }}
                        onBlur={(e) => {
                          if (!growthError) e.currentTarget.style.borderColor = "#D1D5DB";
                        }}
                      />
                      {growthError && (
                        <p style={{ fontSize: "11px", color: "#EF4444", margin: "4px 0 0 0" }}>
                          {growthError}
                        </p>
                      )}
                      <p style={{ fontSize: "11px", color: "#9CA3AF", margin: "4px 0 0 0" }}>
                        Outer band multiplier 
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </Box>

        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
            <label
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Exclusion Filters
            </label>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExcludeSameBrand}
                  onChange={(e) => onExcludeSameBrandChange(e.target.checked)}
                  sx={{
                    color: "#D1D5DB",
                    "&.Mui-checked": { color: "#068E75" },
                    padding: "4px",
                  }}
                />
              }
              label={
                <span style={{ fontSize: "13px", color: "#374151" }}>
                  Exclude same brand competitors
                </span>
              }
              sx={{ marginLeft: "-4px", marginBottom: "4px" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExcludeSpreetailAsin}
                  onChange={(e) => {
                    console.log("[MW-SPREETAIL] Checkbox toggled →", e.target.checked);
                    onExcludeSpreetailAsinChange(e.target.checked);
                  }}
                  sx={{
                    color: "#D1D5DB",
                    "&.Mui-checked": { color: "#068E75" },
                    padding: "4px",
                  }}
                />
              }
              label={
                <span style={{ fontSize: "13px", color: "#374151" }}>
                  Exclude Spreetail-managed ASINs
                </span>
              }
              sx={{ marginLeft: "-4px" }}
            />
          </Box>

        {/* Seasonality checkbox hidden — always-on server-side (2026-04-10) */}
        {false && (
        <Box sx={{ width: "100%", maxWidth: "100%", marginBottom: "20px" }}>
            <label
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Analysis Options
            </label>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isEnabledSeasonality}
                  onChange={(e) => onEnabledSeasonalityChange(e.target.checked)}
                  sx={{
                    color: "#D1D5DB",
                    "&.Mui-checked": { color: "#068E75" },
                    padding: "4px",
                  }}
                />
              }
              label={
                <span style={{ fontSize: "13px", color: "#374151" }}>
                  Enable Seasonality
                </span>
              }
              sx={{ marginLeft: "-4px", marginBottom: "4px" }}
            />
          </Box>
        )}
      </div>

      {/* Fixed Footer with Action Buttons */}
      <div
        style={{
          padding: "10px 16px",
          display: "flex",
          gap: "12px",
          width: "100%",
          boxSizing: "border-box",
          flexShrink: 0,
          borderTop: "1px solid #FFFFFF",
          background: "linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%)",
        }}
      >
        {/* Clear Filter Button */}
        <button
          onClick={onClearFilters}
          disabled={loading}
          style={{
            flex: 1,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            border: "1.5px solid #068E75",
            borderRadius: "8px",
            color: "#068E75",
            fontSize: "14px",
            fontWeight: 600,
            padding: "8px 16px",
            textTransform: "none",
            height: "38px",
            cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            opacity: loading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.8)";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.6)";
            }
          }}
        >
          Reset
        </button>

        {/* Apply Filter Button */}
        <button
          onClick={onApplyFilter}
          disabled={loading || !hasSelectedAsins || hasClusterErrors}
          style={{
            flex: 1,
            backgroundColor: !hasSelectedAsins || hasClusterErrors ? "#9CA3AF" : "#068E75",
            border: "none",
            borderRadius: "8px",
            color: "#FFFFFF",
            fontSize: "14px",
            fontWeight: 600,
            padding: "8px 16px",
            textTransform: "none",
            height: "38px",
            cursor: loading || !hasSelectedAsins || hasClusterErrors ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            opacity: loading || !hasSelectedAsins || hasClusterErrors ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading && hasSelectedAsins && !hasClusterErrors) {
              e.currentTarget.style.backgroundColor = "#057A63";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && hasSelectedAsins && !hasClusterErrors) {
              e.currentTarget.style.backgroundColor = "#068E75";
            }
          }}
        >
          {loading ? "Executing..." : "Execute"}
        </button>
      </div>
    </div>
  );
};

export default MarketWatchFilterView;
